import React, { useState, useEffect } from 'react';
import { CanvasElement } from './templateSerializer';

interface ElementConfigModalProps {
  show: boolean;
  element: CanvasElement | null;
  maxCols: number;
  onSave: (updatedElement: CanvasElement) => void;
  onClose: () => void;
}

const ElementConfigModal: React.FC<ElementConfigModalProps> = ({
  show,
  element,
  maxCols,
  onSave,
  onClose,
}) => {
  const [formData, setFormData] = useState<Partial<CanvasElement>>({});

  useEffect(() => {
    if (element) {
      setFormData({ ...element });
    }
  }, [element]);

  if (!show || !element) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData && element) {
      onSave({ ...element, ...formData } as CanvasElement);
    }
  };

  const keyTypeNormalized = (element.keyType || 'text').toLowerCase();

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
      tabIndex={-1}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow-lg border-0 rounded-4">
          <div className="modal-header bg-light border-bottom-0 pb-3">
            <h5 className="modal-title fw-bold text-dark d-flex align-items-center">
              <i className="fas fa-sliders-h me-2 text-primary"></i>
              Configure Element Settings
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body py-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Element Header Info */}
              <div className="p-3 mb-4 rounded-3 bg-light border">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="badge bg-primary me-2 text-uppercase">
                      {element.type === 'key' ? `Key: ${keyTypeNormalized}` : element.type}
                    </span>
                    <strong className="text-dark">
                      {element.keyName || element.content || element.keyCode || 'Element'}
                    </strong>
                  </div>
                  {element.keyCode && (
                    <code className="text-secondary small bg-white px-2 py-1 rounded border">
                      ${`{${element.keyCode}}`}
                    </code>
                  )}
                </div>
              </div>

              <div className="row g-3">
                {/* Column Span */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold small text-muted">Column Span</label>
                  <select
                    className="form-select form-select-sm"
                    value={formData.colSpan || 1}
                    onChange={(e) => setFormData({ ...formData, colSpan: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 Column</option>
                    {maxCols >= 2 && <option value={2}>2 Columns</option>}
                    {maxCols >= 3 && <option value={3}>3 Columns</option>}
                    {maxCols >= 4 && <option value={4}>4 Columns</option>}
                    {maxCols >= 6 && <option value={6}>6 Columns</option>}
                    <option value={maxCols}>Full Width ({maxCols} Columns)</option>
                  </select>
                </div>

                {/* KEY TYPE SPECIFIC SETTINGS */}
                {element.type === 'key' && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold small text-muted">Show Label</label>
                      <div className="form-check form-switch mt-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="showLabelSwitch"
                          checked={formData.showLabel !== false}
                          onChange={(e) => setFormData({ ...formData, showLabel: e.target.checked })}
                        />
                        <label className="form-check-label small" htmlFor="showLabelSwitch">
                          Display label text above value
                        </label>
                      </div>
                    </div>

                    {formData.showLabel !== false && (
                      <div className="col-12">
                        <label className="form-label fw-semibold small text-muted">
                          Custom Label Text (Optional)
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={`Default: \${${element.keyCode || 'KEY'}_LABEL}`}
                          value={formData.customLabel || ''}
                          onChange={(e) => setFormData({ ...formData, customLabel: e.target.value })}
                        />
                      </div>
                    )}

                    {/* PHOTO SPECIFIC SETTINGS */}
                    {keyTypeNormalized === 'photo' && (
                      <>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold small text-muted">Photo Height</label>
                          <select
                            className="form-select form-select-sm"
                            value={formData.photoHeight || 'h-64'}
                            onChange={(e) => setFormData({ ...formData, photoHeight: e.target.value })}
                          >
                            <option value="h-32">Small (128px / h-32)</option>
                            <option value="h-48">Medium (192px / h-48)</option>
                            <option value="h-64">Large (256px / h-64)</option>
                            <option value="h-80">Extra Large (320px / h-80)</option>
                            <option value="h-96">Huge (384px / h-96)</option>
                            <option value="h-auto">Auto Height</option>
                          </select>
                        </div>

                        <div className="col-md-4">
                          <label className="form-label fw-semibold small text-muted">Object Fit</label>
                          <select
                            className="form-select form-select-sm"
                            value={formData.photoFit || 'object-cover'}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                photoFit: e.target.value as any,
                              })
                            }
                          >
                            <option value="object-cover">Cover (Crop to fill)</option>
                            <option value="object-contain">Contain (Fit inside)</option>
                            <option value="object-fill">Fill (Stretch)</option>
                          </select>
                        </div>

                        <div className="col-md-4">
                          <label className="form-label fw-semibold small text-muted">Border Radius</label>
                          <select
                            className="form-select form-select-sm"
                            value={formData.photoRounded || 'rounded-xl'}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                photoRounded: e.target.value as any,
                              })
                            }
                          >
                            <option value="rounded-none">Square / None</option>
                            <option value="rounded-lg">Rounded Medium (lg)</option>
                            <option value="rounded-xl">Rounded Large (xl)</option>
                            <option value="rounded-2xl">Rounded Extra Large (2xl)</option>
                          </select>
                        </div>
                      </>
                    )}

                    {/* MULTIPLE SPECIFIC SETTINGS */}
                    {keyTypeNormalized === 'multiple' && (
                      <div className="col-md-6">
                        <label className="form-label fw-semibold small text-muted">Gallery Grid Columns</label>
                        <select
                          className="form-select form-select-sm"
                          value={formData.galleryCols || 3}
                          onChange={(e) =>
                            setFormData({ ...formData, galleryCols: parseInt(e.target.value) })
                          }
                        >
                          <option value={2}>2 Columns</option>
                          <option value={3}>3 Columns</option>
                          <option value={4}>4 Columns</option>
                          <option value={6}>6 Columns</option>
                        </select>
                      </div>
                    )}
                  </>
                )}

                {/* HEADING & CUSTOM TEXT SETTINGS */}
                {(element.type === 'heading' || element.type === 'text') && (
                  <div className="col-12">
                    <label className="form-label fw-semibold small text-muted">Text Content</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.content || ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Enter heading or text content..."
                    />
                  </div>
                )}

                {element.type === 'heading' && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold small text-muted">Heading Tag</label>
                      <select
                        className="form-select form-select-sm"
                        value={formData.headingLevel || 'h3'}
                        onChange={(e) => setFormData({ ...formData, headingLevel: e.target.value as any })}
                      >
                        <option value="h1">H1 (Main Title)</option>
                        <option value="h2">H2 (Section Header)</option>
                        <option value="h3">H3 (Subheader)</option>
                        <option value="h4">H4 (Small Heading)</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold small text-muted">Styling</label>
                      <div className="form-check form-switch mt-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="uppercaseSwitch"
                          checked={formData.uppercase !== false}
                          onChange={(e) => setFormData({ ...formData, uppercase: e.target.checked })}
                        />
                        <label className="form-check-label small" htmlFor="uppercaseSwitch">
                          Uppercase Text (UPPERCASE)
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {/* COMMON TEXT STYLING FOR TEXT & KEY TEXT */}
                {(element.type === 'text' ||
                  (element.type === 'key' && keyTypeNormalized === 'text')) && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold small text-muted">Font Size</label>
                      <select
                        className="form-select form-select-sm"
                        value={formData.textSize || 'text-sm'}
                        onChange={(e) => setFormData({ ...formData, textSize: e.target.value })}
                      >
                        <option value="text-xs">Extra Small (text-xs)</option>
                        <option value="text-sm">Small (text-sm)</option>
                        <option value="text-base">Base (text-base)</option>
                        <option value="text-lg">Large (text-lg)</option>
                        <option value="text-xl">Extra Large (text-xl)</option>
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-semibold small text-muted">Font Weight</label>
                      <select
                        className="form-select form-select-sm"
                        value={formData.fontWeight || 'font-semibold'}
                        onChange={(e) => setFormData({ ...formData, fontWeight: e.target.value })}
                      >
                        <option value="font-normal">Normal</option>
                        <option value="font-medium">Medium</option>
                        <option value="font-semibold">Semibold</option>
                        <option value="font-bold">Bold</option>
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-semibold small text-muted">Text Color</label>
                      <select
                        className="form-select form-select-sm"
                        value={formData.textColor || 'text-gray-800'}
                        onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                      >
                        <option value="text-gray-800">Dark Gray (default)</option>
                        <option value="text-gray-900">Black</option>
                        <option value="text-gray-500">Muted Gray</option>
                        <option value="text-primary">Primary Blue</option>
                        <option value="text-emerald-600">Green</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="modal-footer bg-light border-top-0 pt-2">
              <button type="button" className="btn btn-sm btn-outline-secondary px-3" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-sm btn-primary px-4 fw-semibold">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ElementConfigModal;
